'use client';

import { Button } from '@ui/components/button';
import { useMediaQuery } from '@ui/hooks/useMediaQuery';
import { cn } from '@ui/lib/utils';
import { text } from '@modules/garden-planner/text';
import type { ReactNode } from 'react';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faArrowLeft } from '@micks-yard/icons/solid/faArrowLeft';

interface SidebarViewProps {
  isSidebarOpen: boolean;
  onCloseSidebar: () => void;
  onNavigateBack: () => void;
  shareButton: ReactNode;
  gardenTools: ReactNode;
  gridControls: ReactNode;
}

export function SidebarView({
  isSidebarOpen,
  onCloseSidebar,
  onNavigateBack,
  shareButton,
  gardenTools,
  gridControls,
}: SidebarViewProps) {
  const isMd = useMediaQuery('(min-width: 768px)');
  const showSidebar = isMd || isSidebarOpen;

  return (
    <>
      {!isMd && isSidebarOpen ? (
        <button
          type="button"
          aria-label={text.closeSidebar}
          className="fixed inset-0 z-40 bg-black/50"
          onClick={onCloseSidebar}
        />
      ) : null}

      <aside
        className={cn(
          'border-border bg-card shadow-app-sm fixed top-16 left-0 z-50 flex w-64 flex-col border-r transition-transform duration-200',
          showSidebar ? 'translate-x-0' : '-translate-x-64'
        )}
        style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="flex flex-1 flex-col overflow-y-auto px-4 py-6">
          <div className="flex flex-col gap-4">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={onNavigateBack}>
              <FaIcon icon={faArrowLeft} className="size-4" />
              {text.backToMyGardens}
            </Button>

            {shareButton}

            <div className="border-border mt-4 flex flex-col gap-3 border-t pt-4">
              <h2 className="text-foreground text-base font-semibold">{text.gardenTools}</h2>
              {gardenTools}
            </div>

            {gridControls}
          </div>
        </div>
      </aside>
    </>
  );
}
